/**
 * L'écran d'administration des carrières du groupe.
 *
 * *Demandé par le métier le 12/08/2026 : « il faut avoir un référentiel pour les
 * carrières avec dénomination, adresse », et « prévoir d'ajouter ou supprimer,
 * exporter et réimporter avec un format bien défini ».*
 *
 * C'est la liste dans laquelle le conducteur puisera quand il cochera « matériau
 * du groupe » sur une ligne de contre-étude. Elle sert trois lectures d'une même
 * donnée : ce qui arrive au chantier, ce que la carrière doit produire, et d'où
 * part le camion — d'où l'adresse, qui n'est pas décorative.
 *
 * Les partis pris sont ceux des autres référentiels du produit : le **code ne se
 * modifie pas** (il désigne la fiche dans les classeurs), **retirer n'efface
 * pas**, et **les lignes retirées restent affichées**, grisées.
 *
 * La suppression franche existe quand même, et seulement quand rien ne désigne la
 * carrière : sans elle, la fiche saisie de travers il y a deux minutes resterait à
 * vie dans un référentiel qu'on n'ose plus corriger.
 */
import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import {
  ADRESSE_CLASSEUR_CARRIERES,
  chargerCarrieres,
  creerCarriere,
  importerCarrieres,
  modifierCarriere,
  supprimerCarriere,
} from "./api";
import type { BilanImportCarrieres, Carriere, NatureSite } from "./api";

/**
 * Une seule des deux coordonnées est renseignée.
 *
 * Un point a besoin des deux. La 045 ne l'impose pas en base — un référentiel enregistre à la
 * sortie du champ, donc la contrainte ferait échouer le premier des deux gestes — et c'est
 * donc ici que l'état incomplet doit se voir. Le silence, lui, se lirait « c'est fait ».
 */
function coordonneeSeule(c: Carriere): boolean {
  return (c.latitude === null) !== (c.longitude === null);
}

type Etat =
  | { phase: "chargement" }
  | { phase: "chargee"; carrieres: readonly Carriere[] }
  | { phase: "erreur"; raison: string };

/** Un champ qui n'envoie sa valeur qu'une fois la saisie terminée. */
function Champ({
  valeur,
  placeholder,
  className,
  enAttente,
  onValider,
}: {
  valeur: string;
  placeholder?: string;
  /** Une classe de plus, pour signaler un état — une coordonnée seule, par exemple. */
  className?: string;
  enAttente: boolean;
  onValider: (valeur: string) => void;
}): JSX.Element {
  const [brouillon, setBrouillon] = useState<string | null>(null);

  return (
    <input
      className={`saisie saisie--texte${enAttente ? " saisie--attente" : ""}${className === undefined ? "" : ` ${className}`}`}
      value={brouillon ?? valeur}
      placeholder={placeholder}
      disabled={enAttente}
      onChange={(e) => {
        setBrouillon(e.target.value);
      }}
      onFocus={(e) => {
        e.target.select();
      }}
      onBlur={() => {
        const saisi = brouillon;
        setBrouillon(null);
        if (saisi !== null && saisi.trim() !== valeur) onValider(saisi.trim());
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setBrouillon(null);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

function Ajout({
  onAjouter,
}: {
  onAjouter: (
    code: string,
    denomination: string,
    nature: NatureSite,
    commune: string,
  ) => void;
}): JSX.Element {
  const [code, setCode] = useState("");
  const [denomination, setDenomination] = useState("");
  // Carrière par défaut : c'est la nature de tout ce qui existait avant la 039, et le cas de
  // loin le plus fréquent. Le choix est dans le formulaire plutôt qu'après coup, sinon
  // ajouter un dépôt demanderait deux gestes et le second s'oublierait.
  const [nature, setNature] = useState<NatureSite>("carriere");
  const [commune, setCommune] = useState("");

  const complet = code.trim() !== "" && denomination.trim() !== "";

  return (
    <form
      className="ajout"
      onSubmit={(e) => {
        e.preventDefault();
        if (!complet) return;
        onAjouter(code.trim(), denomination.trim(), nature, commune.trim());
        setCode("");
        setDenomination("");
        setNature("carriere");
        setCommune("");
      }}
    >
      <input
        placeholder="Code"
        value={code}
        onChange={(e) => {
          setCode(e.target.value);
        }}
        aria-label="Code du nouveau site"
      />
      <input
        placeholder="Dénomination"
        className="ajout__libelle"
        value={denomination}
        onChange={(e) => {
          setDenomination(e.target.value);
        }}
        aria-label="Dénomination du nouveau site"
      />
      <select
        className="saisie"
        value={nature}
        aria-label="Nature du nouveau site"
        onChange={(e) => {
          setNature(e.target.value === "depot" ? "depot" : "carriere");
        }}
      >
        <option value="carriere">carrière</option>
        <option value="depot">dépôt</option>
      </select>
      <input
        placeholder="Commune"
        value={commune}
        onChange={(e) => {
          setCommune(e.target.value);
        }}
        aria-label="Commune"
      />
      <button type="submit" disabled={!complet}>
        Ajouter
      </button>
    </form>
  );
}

/** Ce que le réimport a fait, dit en une phrase que personne n'a à interpréter. */
function Bilan({ bilan }: { bilan: BilanImportCarrieres }): JSX.Element {
  return (
    <div className="etat">
      <strong>
        {bilan.ajoutees} ajoutée(s), {bilan.modifiees} modifiée(s), {bilan.inchangees} inchangée(s).
      </strong>{" "}
      Une carrière absente du classeur n'est jamais supprimée — pour la retirer, écrire « non »
      dans la colonne <em>Actif</em>, ou la retirer ici.
      {bilan.refusees.length > 0 && (
        <ul className="alerte">
          {bilan.refusees.map((r) => (
            <li key={`${String(r.ligne)}-${r.raison}`}>
              ligne {r.ligne} du classeur : {r.raison}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Carrieres(): JSX.Element {
  const [etat, setEtat] = useState<Etat>({ phase: "chargement" });
  const [enAttente, setEnAttente] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [bilan, setBilan] = useState<BilanImportCarrieres | null>(null);
  const [montrerRetirees, setMontrerRetirees] = useState(false);
  const champFichier = useRef<HTMLInputElement>(null);

  const recharger = (): void => {
    chargerCarrieres()
      .then(({ carrieres }) => {
        setEtat({ phase: "chargee", carrieres });
      })
      .catch((e: unknown) => {
        setEtat({ phase: "erreur", raison: e instanceof Error ? e.message : String(e) });
      });
  };

  useEffect(recharger, []);

  const agir = (id: string | null, action: Promise<unknown>): void => {
    setEnAttente(id);
    setErreur(null);
    action
      .then(recharger)
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setEnAttente(null);
      });
  };

  if (etat.phase === "chargement") return <p>Chargement des carrières…</p>;
  if (etat.phase === "erreur") return <p className="alerte">{etat.raison}</p>;

  const retirees = etat.carrieres.filter((c) => !c.actif).length;
  const affichees = montrerRetirees ? etat.carrieres : etat.carrieres.filter((c) => c.actif);

  return (
    <section className="referentiel">
      <h2>
        Carrières et sites du groupe — {etat.carrieres.length - retirees} actif(s)
        {retirees > 0 && (
          <>
            {" · "}
            <button
              type="button"
              className="lien"
              onClick={() => {
                setMontrerRetirees(!montrerRetirees);
              }}
            >
              {montrerRetirees ? "masquer" : "voir"} les {retirees} retiré(s)
            </button>
          </>
        )}
      </h2>

      <p className="note">
        La provenance d'un matériau se coche sur la ligne de contre-étude, pas ici : c'est le
        conducteur qui décide, chantier par chantier, si un matériau vient du groupe et de quel
        site. Cette liste lui donne le choix.
      </p>
      <p className="note">
        Une <strong>carrière</strong> produit du matériau : elle a une colonne dans la grille
        tarifaire. Un <strong>dépôt</strong> stocke sans produire — une plateforme, un stock
        d'articles — et n'en a pas. Les deux se proposent de la même façon sur une ligne et sur
        une pastille.
      </p>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      <Ajout
        onAjouter={(code, denomination, nature, commune) => {
          agir(null, creerCarriere({ code, denomination, nature, commune }));
        }}
      />

      {/*
        L'aller-retour par Excel. Le classeur exporté EST le format attendu au
        retour : c'est la seule façon d'être sûr que ce qui sort rentre.
      */}
      <p className="etat">
        <a href={ADRESSE_CLASSEUR_CARRIERES} download>
          Exporter le classeur
        </a>
        {" · "}
        <button
          type="button"
          className="lien"
          onClick={() => champFichier.current?.click()}
        >
          Réimporter un classeur
        </button>
        <input
          ref={champFichier}
          type="file"
          accept=".xlsx"
          hidden
          onChange={(e) => {
            const fichier = e.target.files?.[0];
            e.target.value = "";
            if (fichier === undefined) return;
            setBilan(null);
            setErreur(null);
            importerCarrieres(fichier)
              .then((resultat) => {
                setBilan(resultat);
                recharger();
              })
              .catch((err: unknown) => {
                setErreur(err instanceof Error ? err.message : String(err));
              });
          }}
        />
      </p>

      {bilan !== null && <Bilan bilan={bilan} />}

      {/*
        Le conteneur de défilement, comme les trois autres référentiels depuis le 01/09/2026.

        C'est lui qui rend la grille ci-dessous effective : la règle
        `.referentiel__defilement table` pose `width: min-content`, ce qui pour une table en
        `table-layout: fixed` vaut la SOMME des largeurs du `colgroup`. Sans ce conteneur, le
        `width: 100%` de `table.ressources` continuerait de partager la fenêtre à parts égales
        et la grille ne servirait à rien — c'est exactement ce que le commentaire de la 01/09
        annonçait de ce tableau-ci, qui portait la classe de section sans avoir de grille.

        Et il fait défiler le TABLEAU seul : sans lui, c'est la page qui partirait de côté, et
        la colonne du code — la seule qui dise de quelle ligne il s'agit — sortirait de l'écran.
      */}
      <div className="referentiel__defilement">
      <table className="ressources">
        {/*
          LA GRILLE DES COLONNES, en `colgroup`.

          `table.ressources` impose `table-layout: fixed` sur `width: 100%` : une mise en page
          qui ne lit les largeurs **que** sur un `colgroup` ou sur la première rangée. Ce
          tableau n'en avait aucun — c'est le défaut que le 01/09 a réparé sur les trois autres
          référentiels, et il restait ici. Mesuré au navigateur avant d'y toucher : dix colonnes
          se partageaient la fenêtre à parts égales, donc « Dénomination » recevait 115 px, la
          largeur d'un code postal.

          Les largeurs sont **mesurées** sur les dénominations réelles du référentiel de
          l'installation, lues dans le classeur qui a amorcé les tables de distances : 37
          caractères au plus long (« Plateforme Revalorisation ROY TRAVAUX »). Ce sont des
          minima, pas la vérité — ce référentiel s'administre à l'écran, et une ligne ajoutée
          demain peut être plus longue.
        */}
        <colgroup>
          <col style={{ width: "calc(9ch + 0.7rem)" }} />
          <col style={{ width: "calc(38ch + 0.7rem)" }} />
          <col style={{ width: "7rem" }} />
          {/* La case « site de stockage » (078) : une case à cocher, rien de plus large. */}
          <col style={{ width: "6rem" }} />
          <col style={{ width: "calc(24ch + 0.7rem)" }} />
          <col style={{ width: "calc(11ch + 0.7rem)" }} />
          <col style={{ width: "calc(18ch + 0.7rem)" }} />
          <col style={{ width: "calc(11ch + 0.7rem)" }} />
          <col style={{ width: "calc(11ch + 0.7rem)" }} />
          <col style={{ width: "calc(16ch + 0.7rem)" }} />
          <col style={{ width: "9rem" }} />
        </colgroup>
        <thead>
          <tr>
            <th>Code</th>
            <th>Dénomination</th>
            <th>Nature</th>
            <th>Stockage</th>
            <th>Adresse</th>
            <th>Code postal</th>
            <th>Commune</th>
            <th>Latitude</th>
            <th>Longitude</th>
            <th>Note</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {affichees.map((c) => (
            <tr key={c.id} className={c.actif ? undefined : "retiree"}>
              <td className="code">{c.code}</td>
              <td>
                <Champ
                  valeur={c.denomination}
                  enAttente={enAttente === c.id}
                  onValider={(valeur) => {
                    agir(c.id, modifierCarriere(c.id, { denomination: valeur }));
                  }}
                />
              </td>
              {/*
                La nature (039). Une liste fermée et non un champ libre : elle commande la
                grille tarifaire, et « depôt » mal orthographié y ferait apparaître une
                colonne que personne n'attendait.
              */}
              <td>
                <select
                  className="saisie"
                  value={c.nature}
                  disabled={enAttente === c.id}
                  title="Une carrière produit du matériau et a une colonne dans la grille tarifaire ; un dépôt stocke sans produire."
                  onChange={(e) => {
                    agir(
                      c.id,
                      modifierCarriere(c.id, {
                        nature: e.target.value === "depot" ? "depot" : "carriere",
                      }),
                    );
                  }}
                >
                  <option value="carriere">carrière</option>
                  <option value="depot">dépôt</option>
                </select>
              </td>
              {/*
                LA CASE « SITE DE STOCKAGE » (078), demandée par Chris le 15/09/2026.

                Indépendante de la nature, qui commande la grille tarifaire : une carrière peut
                tenir un magasin, un dépôt peut n'en tenir aucun. C'est cette case — et elle seule
                — qui ouvre le site au module de stock : entrées, sorties, réservations,
                inventaires. Tant qu'aucune n'est cochée, le module le dit et renvoie ici.
              */}
              <td>
                <input
                  type="checkbox"
                  checked={c.stocke}
                  disabled={enAttente === c.id}
                  aria-label={`${c.denomination} tient un magasin`}
                  title="Ce site tient un magasin : on y entre et on y sort des articles. Indépendant de la nature."
                  onChange={(e) => {
                    agir(c.id, modifierCarriere(c.id, { stocke: e.target.checked }));
                  }}
                />
              </td>
              <td>
                <Champ
                  valeur={c.adresse ?? ""}
                  placeholder="—"
                  enAttente={enAttente === c.id}
                  onValider={(valeur) => {
                    agir(c.id, modifierCarriere(c.id, { adresse: valeur }));
                  }}
                />
              </td>
              <td>
                <Champ
                  valeur={c.codePostal ?? ""}
                  placeholder="—"
                  enAttente={enAttente === c.id}
                  onValider={(valeur) => {
                    agir(c.id, modifierCarriere(c.id, { codePostal: valeur }));
                  }}
                />
              </td>
              <td>
                <Champ
                  valeur={c.commune ?? ""}
                  placeholder="—"
                  enAttente={enAttente === c.id}
                  onValider={(valeur) => {
                    agir(c.id, modifierCarriere(c.id, { commune: valeur }));
                  }}
                />
              </td>
              {/*
                LE POINT DE CHARGEMENT (045), saisi — pas déduit de l'adresse.

                Sur une carrière, le point utile n'est pas l'adresse postale : c'est la
                bascule ou l'entrée du site, qui n'a pas de numéro dans la rue. Les valeurs se
                collent le plus souvent depuis une carte, et la virgule décimale est acceptée :
                un clavier français la produit.

                Le liseré signale une coordonnée SEULE. Le serveur l'accepte — un référentiel
                enregistre à la sortie du champ, donc exiger le couple ferait échouer le
                premier des deux gestes —, mais une latitude sans longitude ne désigne rien et
                ne doit pas se lire comme une saisie faite.
              */}
              <td>
                <Champ
                  valeur={c.latitude ?? ""}
                  placeholder="43.7102"
                  className={coordonneeSeule(c) ? "saisie--a-remplir" : undefined}
                  enAttente={enAttente === c.id}
                  onValider={(valeur) => {
                    agir(c.id, modifierCarriere(c.id, { latitude: valeur }));
                  }}
                />
              </td>
              <td>
                <Champ
                  valeur={c.longitude ?? ""}
                  placeholder="-1.0503"
                  className={coordonneeSeule(c) ? "saisie--a-remplir" : undefined}
                  enAttente={enAttente === c.id}
                  onValider={(valeur) => {
                    agir(c.id, modifierCarriere(c.id, { longitude: valeur }));
                  }}
                />
              </td>
              <td>
                <Champ
                  valeur={c.note ?? ""}
                  placeholder="—"
                  enAttente={enAttente === c.id}
                  onValider={(valeur) => {
                    agir(c.id, modifierCarriere(c.id, { note: valeur }));
                  }}
                />
              </td>
              <td>
                <button
                  type="button"
                  className="lien"
                  disabled={enAttente === c.id}
                  onClick={() => {
                    agir(c.id, modifierCarriere(c.id, { actif: !c.actif }));
                  }}
                >
                  {c.actif ? "Retirer" : "Remettre"}
                </button>
                {" · "}
                {/*
                  La suppression ne demande pas confirmation : le serveur REFUSE
                  d'effacer une carrière que des contre-études désignent, et le
                  seul cas qui passe est donc la fiche que personne n'emploie.
                  Une boîte de dialogue devant une action sans conséquence
                  apprend surtout à cliquer « oui » sans lire.
                */}
                <button
                  type="button"
                  className="lien"
                  disabled={enAttente === c.id}
                  onClick={() => {
                    agir(c.id, supprimerCarriere(c.id));
                  }}
                >
                  Supprimer
                </button>
              </td>
            </tr>
          ))}
          {affichees.length === 0 && (
            <tr>
              <td colSpan={11} className="vide">
                Aucune carrière. En ajouter une, ou réimporter un classeur.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
    </section>
  );
}
